import { supabase } from '../lib/supabase';

import { notificationEngineService } from './notificationEngineService';

export const complianceAlertTriggerService = {
  // ============================================================================
  // COMPLIANCE KEYWORDS MAPPING
  // ============================================================================

  complianceKeywords: {
    minimum_wage: ['minimum wage', 'wage increase', 'wage adjustment', 'wage rate', 'salary floor'],
    nasfund: ['nasfund', 'superannuation', 'retirement fund', 'pension contribution', 'employer contribution'],
    employment_law: ['employment act', 'labour law', 'employment regulation', 'worker rights', 'employment standard'],
    tax_regulation: ['tax rate', 'paye', 'salary tax', 'income tax', 'tax withholding', 'tax regulation', 'tax compliance']
  },

  // ============================================================================
  // NEWS MONITORING & ALERT TRIGGERING
  // ============================================================================

  async analyzeNewsForCompliance(newsArticle) {
    const triggers = [];
    const content = `${newsArticle?.title} ${newsArticle?.summary} ${newsArticle?.content}`?.toLowerCase();

    // Check for minimum wage changes
    if (this.containsKeywords(content, this.complianceKeywords?.minimum_wage)) {
      triggers?.push({
        type: 'minimum_wage_change',
        severity: 'critical',
        category: 'payroll'
      });
    }

    // Check for NASFUND rate changes
    if (this.containsKeywords(content, this.complianceKeywords?.nasfund)) {
      triggers?.push({
        type: 'nasfund_rate_change',
        severity: 'high',
        category: 'payroll'
      });
    }

    // Check for employment law changes
    if (this.containsKeywords(content, this.complianceKeywords?.employment_law)) {
      triggers?.push({
        type: 'employment_law_change',
        severity: 'high',
        category: 'compliance'
      });
    }

    // Check for tax regulation changes
    if (this.containsKeywords(content, this.complianceKeywords?.tax_regulation)) {
      triggers?.push({
        type: 'tax_regulation_change',
        severity: 'critical',
        category: 'payroll'
      });
    }

    return triggers;
  },

  containsKeywords(text, keywords) {
    return keywords?.some(keyword => text?.includes(keyword?.toLowerCase()));
  },

  // ============================================================================
  // ALERT CREATION
  // ============================================================================

  async createComplianceAlert(newsArticle, trigger) {
    const alertTitles = {
      minimum_wage_change: 'Minimum Wage Update Detected',
      nasfund_rate_change: 'NASFUND Rate Change Detected',
      employment_law_change: 'Employment Law Update Detected',
      tax_regulation_change: 'Tax Regulation Change Detected'
    };

    const alertMessages = {
      minimum_wage_change: `PNG Government has announced changes to minimum wage regulations. Review: ${newsArticle?.title}`,
      nasfund_rate_change: `NASFUND contribution rates may be affected. Review: ${newsArticle?.title}`,
      employment_law_change: `Employment Act updates detected that may impact your compliance. Review: ${newsArticle?.title}`,
      tax_regulation_change: `Tax regulation changes detected that may affect payroll calculations. Review: ${newsArticle?.title}`
    };

    try {
      // Create system alert
      const { data: alertData, error: alertError } = await supabase
        ?.from('system_alerts')
        ?.insert({
          alert_type: 'compliance_violation',
          severity: trigger?.severity,
          title: alertTitles?.[trigger?.type] || 'Compliance Update Detected',
          message: alertMessages?.[trigger?.type] || `Review news article: ${newsArticle?.title}`,
          entity_type: 'png_news',
          entity_id: newsArticle?.id,
          alert_status: 'unread',
          metadata: {
            news_id: newsArticle?.id,
            news_category: newsArticle?.category,
            trigger_type: trigger?.type,
            publication_date: newsArticle?.publicationDate,
            effective_date: newsArticle?.effectiveDate,
            source_url: newsArticle?.sourceUrl
          },
          deadline: newsArticle?.effectiveDate || null
        })
        ?.select()
        ?.single();

      if (alertError) throw alertError;

      // Create notification for routing
      await notificationEngineService?.createNotification({
        notificationType: 'compliance_alert',
        priority: trigger?.severity === 'critical' ? 'high' : 'medium',
        recipientRole: 'admin',
        subject: alertTitles?.[trigger?.type] || 'Compliance Update',
        message: alertMessages?.[trigger?.type] || `Review news article: ${newsArticle?.title}`,
        entityType: 'system_alert',
        entityId: alertData?.id,
        metadata: {
          alert_id: alertData?.id,
          news_id: newsArticle?.id,
          trigger_type: trigger?.type,
          news_url: newsArticle?.sourceUrl
        }
      });

      return alertData;
    } catch (error) {
      console.error('Error creating compliance alert:', error);
      throw error;
    }
  },

  // ============================================================================
  // BATCH PROCESSING
  // ============================================================================

  async processNewsArticle(newsArticle) {
    try {
      const triggers = await this.analyzeNewsForCompliance(newsArticle);

      if (triggers?.length > 0) {
        const alerts = [];
        for (const trigger of triggers) {
          const alert = await this.createComplianceAlert(newsArticle, trigger);
          alerts?.push(alert);
        }
        return alerts;
      }

      return [];
    } catch (error) {
      console.error('Error processing news article:', error);
      throw error;
    }
  },

  async processMultipleNews(newsArticles) {
    const results = [];
    for (const article of newsArticles) {
      try {
        const alerts = await this.processNewsArticle(article);
        if (alerts?.length > 0) {
          results?.push({ newsId: article?.id, alerts });
        }
      } catch (error) {
        console.error(`Error processing news ${article?.id}:`, error);
      }
    }
    return results;
  },

  // ============================================================================
  // REALTIME MONITORING
  // ============================================================================

  subscribeToNewsUpdates(callback) {
    const channel = supabase
      ?.channel('compliance_news_monitor')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'png_government_news',
          filter: 'is_published=eq.true'
        },
        async (payload) => {
          const newNews = {
            id: payload?.new?.id,
            title: payload?.new?.title,
            summary: payload?.new?.summary,
            content: payload?.new?.content,
            category: payload?.new?.category,
            priority: payload?.new?.priority,
            publicationDate: payload?.new?.publication_date,
            effectiveDate: payload?.new?.effective_date,
            sourceUrl: payload?.new?.source_url
          };

          // Automatically process new news for compliance triggers
          const alerts = await this.processNewsArticle(newNews);
          
          if (callback && alerts?.length > 0) {
            callback(newNews, alerts);
          }
        }
      )
      ?.subscribe();

    return channel;
  },

  unsubscribeFromNewsUpdates(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  // ============================================================================
  // STATISTICS & REPORTING
  // ============================================================================

  async getComplianceAlertStats(dateFrom, dateTo) {
    const { data, error } = await supabase
      ?.from('system_alerts')
      ?.select('metadata, severity, created_at')
      ?.eq('entity_type', 'png_news')
      ?.gte('created_at', dateFrom)
      ?.lte('created_at', dateTo);

    if (error) throw error;

    const stats = {
      total: data?.length || 0,
      byTriggerType: {
        minimum_wage_change: 0,
        nasfund_rate_change: 0,
        employment_law_change: 0,
        tax_regulation_change: 0
      },
      bySeverity: {
        critical: data?.filter(a => a?.severity === 'critical')?.length || 0,
        high: data?.filter(a => a?.severity === 'high')?.length || 0,
        medium: data?.filter(a => a?.severity === 'medium')?.length || 0
      }
    };

    data?.forEach(alert => {
      const triggerType = alert?.metadata?.trigger_type;
      if (triggerType && stats?.byTriggerType?.hasOwnProperty(triggerType)) {
        stats.byTriggerType[triggerType]++;
      }
    });

    return stats;
  }
};
